/**
 * Utility functions for file preview handling
 */

/**
 * Check if file is a text file
 */
export const isTextFile = (fileName: string): boolean => {
  const textExtensions = [
    '.txt',
    '.json',
    '.js',
    '.ts',
    '.tsx',
    '.jsx',
    '.py',
    '.csv',
    '.html',
    '.css',
    '.md',
    '.xml',
    '.sql',
    '.sh',
    '.bash',
    '.yml',
    '.yaml',
    '.toml',
    '.ini',
    '.log',
  ];

  const extension = fileName
    .substring(fileName.lastIndexOf('.'))
    .toLowerCase();
  return textExtensions.includes(extension);
};

/**
 * Check if file is an image file
 */
export const isImageFile = (fileName: string): boolean => {
  const imageExtensions = [
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.bmp',
    '.webp',
    '.svg',
    '.ico',
  ];

  const extension = fileName
    .substring(fileName.lastIndexOf('.'))
    .toLowerCase();
  return imageExtensions.includes(extension);
};

/**
 * Check if file is a folder
 */
export const isFolder = (file: any): boolean => {
  return file.type === 'folder' || file.isFolder === true;
};

/**
 * Get file type from file name and properties
 */
export const getFileType = (
  fileName: string,
  fileType?: string
): 'text' | 'image' | 'folder' | 'unknown' => {
  if (isFolder({ type: fileType })) {
    return 'folder';
  }
  if (isImageFile(fileName)) {
    return 'image';
  }
  if (isTextFile(fileName)) {
    return 'text';
  }
  return 'unknown';
};

/**
 * Load image file as data URL
 */
export const loadImageAsDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = () => {
      reject(new Error('Failed to read image file'));
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Load text file content
 */
export const loadTextFile = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = () => {
      reject(new Error('Failed to read text file'));
    };
    reader.readAsText(file);
  });
};

/**
 * Limit text display length for large files
 */
export const truncateText = (
  text: string,
  maxLength: number = 50000
): { text: string; isTruncated: boolean } => {
  if (text.length > maxLength) {
    return {
      text: text.substring(0, maxLength) + '\n\n... [file truncated for preview]',
      isTruncated: true,
    };
  }
  return { text, isTruncated: false };
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get file extension
 */
export const getFileExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1) return '';
  return fileName.substring(lastDot + 1).toLowerCase();
};

/**
 * Get MIME type for file
 */
export const getMimeType = (fileName: string): string => {
  const ext = getFileExtension(fileName);

  const mimeTypes: { [key: string]: string } = {
    txt: 'text/plain',
    json: 'application/json',
    js: 'application/javascript',
    ts: 'application/typescript',
    tsx: 'application/typescript',
    jsx: 'application/javascript',
    py: 'text/x-python',
    csv: 'text/csv',
    html: 'text/html',
    css: 'text/css',
    md: 'text/markdown',
    xml: 'application/xml',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
  };

  return mimeTypes[ext] || 'application/octet-stream';
};

/**
 * Get syntax highlighting language from file extension
 */
export const getHighlightLanguage = (fileName: string): string => {
  const ext = getFileExtension(fileName);

  const languageMap: { [key: string]: string } = {
    js: 'javascript',
    ts: 'typescript',
    tsx: 'typescript',
    jsx: 'javascript',
    py: 'python',
    json: 'json',
    html: 'html',
    css: 'css',
    md: 'markdown',
    xml: 'xml',
    sql: 'sql',
    sh: 'bash',
    bash: 'bash',
    yml: 'yaml',
    yaml: 'yaml',
    toml: 'toml',
  };

  return languageMap[ext] || 'plaintext';
};

/**
 * Check if file is too large to display fully
 */
export const isFileTooLarge = (
  fileSize: number,
  maxSize: number = 5 * 1024 * 1024
): boolean => {
  return fileSize > maxSize;
};

/**
 * Get file name without extension
 */
export const getFileNameWithoutExt = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1) return fileName;
  return fileName.substring(0, lastDot);
};
